import { useRequest } from '@/hooks/useRequest';
import { heroSlides, introHighlights, platformIntro, stats } from '@/mocks/home';
import { fetchPortalSetting } from '@/services/portal';
import { adaptPortalSetting } from '@/services/adapters';
import type { PortalSettingView } from '@/types/content';

/** 接口失败或后端未配置时的兜底数据（mock） */
const fallbackSetting: PortalSettingView = {
  heroSlides,
  stats,
  introHighlights,
  platformIntro,
  enrollNotices: [],
};

let cachedSetting: PortalSettingView | null = null;
let pendingRequest: Promise<PortalSettingView> | null = null;

/** 共享加载官网配置（并发调用复用同一 Promise，成功后缓存，避免首页重复请求） */
function loadPortalSetting(): Promise<PortalSettingView> {
  if (cachedSetting) return Promise.resolve(cachedSetting);

  if (!pendingRequest) {
    pendingRequest = fetchPortalSetting()
      .then((dto) => {
        const setting = adaptPortalSetting(dto);
        // 后端已有任一配置即视为已初始化（避免配置为空时覆盖兜底）
        if (
          setting.heroSlides.length ||
          setting.stats.length ||
          setting.introHighlights.length ||
          setting.platformIntro.title ||
          setting.platformIntro.image
        ) {
          cachedSetting = setting;
        }
        return setting;
      })
      .catch((error) => {
        pendingRequest = null;
        throw error;
      });
  }

  return pendingRequest;
}

/**
 * 官网配置（首页 Hero/统计/平台介绍 共享一次请求）。
 * 某项后端未配置时该项降级为 mock 数据。
 */
export function usePortalSetting(): PortalSettingView {
  const { data } = useRequest(() => loadPortalSetting(), { fallback: fallbackSetting });

  return {
    heroSlides: data.heroSlides.length ? data.heroSlides : fallbackSetting.heroSlides,
    stats: data.stats.length ? data.stats : fallbackSetting.stats,
    introHighlights: data.introHighlights.length ? data.introHighlights : fallbackSetting.introHighlights,
    platformIntro:
      data.platformIntro.title || data.platformIntro.image
        ? data.platformIntro
        : fallbackSetting.platformIntro,
    enrollNotices: data.enrollNotices.length ? data.enrollNotices : fallbackSetting.enrollNotices,
  };
}
